import { useState } from "react";
import { Shield, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTrust } from "@/hooks/useTrust";

interface TrustActionButtonProps {
  userId: string;
  displayName: string;
  canTrustOthers: boolean;
  hasTrustedTarget: boolean;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const TrustActionButton = ({ 
  userId, 
  displayName, 
  canTrustOthers, 
  hasTrustedTarget,
  variant = "default",
  size = "default",
  className 
}: TrustActionButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [comment, setComment] = useState("");
  const { trustUser, trusting } = useTrust(userId);

  // Don't show if user can't trust others or has already trusted this user
  if (!canTrustOthers || hasTrustedTarget) {
    return null;
  }

  const handleTrust = async () => {
    const success = await trustUser(comment.trim() || undefined);
    if (success) {
      setShowDialog(false);
      setComment("");
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={() => setShowDialog(true)}
        disabled={trusting}
      >
        <Shield className="w-4 h-4 mr-2" />
        Trust User
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Trust {displayName}?
            </DialogTitle>
            <DialogDescription className="text-left">
              You should only trust users you've met in person or had verified communication with. 
              This helps build a safer community by confirming real profiles.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="trust-comment">
                Comment (Optional)
              </Label>
              <Textarea
                id="trust-comment"
                placeholder="Why are you trusting this user? (max 100 characters)"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 100))}
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {comment.length}/100 characters
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDialog(false);
                setComment("");
              }}
              disabled={trusting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTrust}
              disabled={trusting}
              className="bg-primary hover:bg-primary/90"
            >
              {trusting ? "Trusting..." : "Confirm Trust"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrustActionButton;